import cx from 'classnames';
import { X } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import globalMessages from 'shared/global/messages';

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export function Sheet({ open, onOpenChange, children }: SheetProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return <>{children}</>;
}

interface SheetContentProps {
  'aria-labelledby'?: string;
  open: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
}

export function SheetContent({
  'aria-labelledby': labelledBy,
  open,
  onClose,
  side = 'left',
  className,
  children,
}: SheetContentProps) {
  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (typeof window === 'undefined') return null;

  return createPortal(
    <>
      <div
        className={cx(
          'fixed inset-0 z-50 bg-black/50 transition-opacity duration-300',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleOverlayClick}
        onMouseDown={e => e.stopPropagation()}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-labelledby={labelledBy}
        aria-modal="true"
        tabIndex={-1}
        className={cx(
          'fixed z-[51] flex h-full w-full flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out sm:max-w-md',
          side === 'left' && 'inset-y-0 left-0',
          side === 'right' && 'inset-y-0 right-0',
          side === 'left' && (open ? 'translate-x-0' : '-translate-x-full'),
          side === 'right' && (open ? 'translate-x-0' : 'translate-x-full'),
          className,
        )}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

interface SheetHeaderProps {
  className?: string;
  children: React.ReactNode;
}

export function SheetHeader({ className, children }: SheetHeaderProps) {
  return (
    <div className={cx('flex items-center justify-between p-4', className)}>
      {children}
    </div>
  );
}

interface SheetTitleProps {
  id?: string;
  className?: string;
  children: React.ReactNode;
}

export function SheetTitle({ id, className, children }: SheetTitleProps) {
  return (
    <h2
      id={id}
      className={cx('text-lg font-semibold text-gray-900', className)}
    >
      {children}
    </h2>
  );
}

interface SheetCloseProps {
  onClose: () => void;
  className?: string;
}

export function SheetClose({ onClose, className }: SheetCloseProps) {
  const intl = useIntl();
  return (
    <button
      type="button"
      onClick={onClose}
      className={cx(
        'rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2',
        className,
      )}
      aria-label={intl.formatMessage(globalMessages.close)}
    >
      <X className="h-5 w-5" />
    </button>
  );
}

interface SheetBodyProps {
  className?: string;
  children: React.ReactNode;
}

export function SheetBody({ className, children }: SheetBodyProps) {
  return (
    <div className={cx('flex-1 overflow-y-auto', className)}>{children}</div>
  );
}

interface SheetFooterProps {
  className?: string;
  children: React.ReactNode;
}

export function SheetFooter({ className, children }: SheetFooterProps) {
  return <div className={cx('p-4', className)}>{children}</div>;
}
